/* eslint-disable @typescript-eslint/ban-ts-comment */
import { computed, ComputedRef } from 'vue'
import {
  useRouter,
  useRoute,
  RouteQueryAndHash,
  RouteLocationOptions,
  LocationAsRelativeRaw
} from 'vue-router'
import {
  AddressStateField,
  AddressStateFieldConfig
} from './fields/AddressStateField'
import {
  BooleanStateField,
  BooleanStateFieldConfig
} from './fields/BooleanStateField'
import {
  CustomStateField,
  CustomStateFieldConfig
} from './fields/CustomStateField'
import {
  DateRangeStateField,
  DateRangeStateFieldConfig
} from './fields/DateRangeStateField'
import { DateStateField, DateStateFieldConfig } from './fields/DateStateField'
import {
  GalleryStateField,
  GalleryStateFieldConfig
} from './fields/GalleryStateField'
import {
  ImageStateField,
  ImageStateFieldConfig
} from './fields/ImageStateField'

import {
  NumberStateField,
  NumberStateFieldConfig
} from './fields/NumberStateField'
import {
  SelectStateField,
  SelectStateFieldConfig
} from './fields/SelectStateField'
import { TextStateField, TextStateFieldConfig } from './fields/TextStateField'
import { StateFieldConfig } from './StateField'
import { F } from 'ts-toolbelt'

export type ViewStateConfigField =
  | TextStateFieldConfig
  | NumberStateFieldConfig
  | ImageStateFieldConfig
  | GalleryStateFieldConfig
  | AddressStateFieldConfig
  | BooleanStateFieldConfig
  | DateStateFieldConfig
  | DateRangeStateFieldConfig
  | SelectStateFieldConfig
  | CustomStateFieldConfig

export type ViewStateField =
  | TextStateField
  | NumberStateField
  | ImageStateField
  | GalleryStateField
  | AddressStateField
  | BooleanStateField
  | DateStateField
  | DateRangeStateField
  | SelectStateField
  | CustomStateField

export type UseViewStateReturnField<C> = C extends TextStateFieldConfig
  ? TextStateField
  : C extends NumberStateFieldConfig
  ? NumberStateField
  : C extends ImageStateFieldConfig
  ? ImageStateField
  : C extends GalleryStateFieldConfig
  ? GalleryStateField
  : C extends AddressStateFieldConfig
  ? AddressStateField
  : C extends BooleanStateFieldConfig
  ? BooleanStateField
  : C extends DateStateFieldConfig
  ? DateStateField
  : C extends DateRangeStateFieldConfig
  ? DateRangeStateField
  : C extends SelectStateFieldConfig
  ? SelectStateField
  : C extends CustomStateFieldConfig
  ? CustomStateField<C['default']>
  : ViewStateField

export type UseViewStateState<C extends StateFieldConfig> = {
  [J in keyof C]: UseViewStateReturnField<C[J]>
}

export type UseViewStateReturn<C> = {
  state: UseViewStateState<C>
  routeStatePush: () => Promise<boolean>
  routeStatePull: () => Promise<void>
  fieldsUpdated: ComputedRef<boolean>
  resetFieldsUpdated: () => void
  resetValues: () => void
  resetErrors: () => void
}

export type UseViewStateConfig = Record<string, ViewStateConfigField>

export function useViewState<T extends UseViewStateConfig>(
  config: F.Narrow<T>
): UseViewStateReturn<T> {
  const router = useRouter()
  const route = useRoute()

  const state = {} as UseViewStateState<T>
  const queryFields = {} as { [key: string]: (keyof T)[] }
  const paramFields = {} as { [key: string]: (keyof T)[] }

  const keys = Object.keys(config)

  for (const configKey of keys) {
    // @ts-ignore
    if (config[configKey].type === 'text') {
      // @ts-ignore
      const cfgField = config[configKey] as TextStateFieldConfig
      // @ts-ignore
      state[configKey] = new TextStateField(cfgField)
      // @ts-ignore
    } else if (config[configKey].type === 'number') {
      // @ts-ignore
      const cfgField = config[configKey] as NumberStateFieldConfig
      // @ts-ignore
      state[configKey] = new NumberStateField(cfgField)
      // @ts-ignore
    } else if (config[configKey].type === 'image') {
      // @ts-ignore
      const cfgField = config[configKey] as ImageStateFieldConfig
      // @ts-ignore
      state[configKey] = new ImageStateField(cfgField)
      // @ts-ignore
    } else if (config[configKey].type === 'gallery') {
      // @ts-ignore
      const cfgField = config[configKey] as GalleryStateFieldConfig
      // @ts-ignore
      state[configKey] = new GalleryStateField(cfgField)
      // @ts-ignore
    } else if (config[configKey].type === 'address') {
      // @ts-ignore
      const cfgField = config[configKey] as AddressStateFieldConfig
      // @ts-ignore
      state[configKey] = new AddressStateField(cfgField)
      // @ts-ignore
    } else if (config[configKey].type === 'boolean') {
      // @ts-ignore
      const cfgField = config[configKey] as BooleanStateFieldConfig
      // @ts-ignore
      state[configKey] = new BooleanStateField(cfgField)
      // @ts-ignore
    } else if (config[configKey].type === 'date') {
      // @ts-ignore
      const cfgField = config[configKey] as DateStateFieldConfig
      // @ts-ignore
      state[configKey] = new DateStateField(cfgField)
      // @ts-ignore
    } else if (config[configKey].type === 'daterange') {
      // @ts-ignore
      const cfgField = config[configKey] as DateRangeStateFieldConfig
      // @ts-ignore
      state[configKey] = new DateRangeStateField(cfgField)
      // @ts-ignore
    } else if (config[configKey].type === 'select') {
      // @ts-ignore
      const cfgField = config[configKey] as SelectStateFieldConfig
      // @ts-ignore
      state[configKey] = new SelectStateField(cfgField)
      // @ts-ignore
    } else if (config[configKey].type === 'custom') {
      // @ts-ignore
      const cfgField = config[configKey] as CustomStateFieldConfig
      // @ts-ignore
      state[configKey] = new CustomStateField(cfgField)
    }

    const cfg = state[configKey]
    if (cfg.routeParam) {
      paramFields[cfg.routeParam]
        ? paramFields[cfg.routeParam].push(configKey)
        : (paramFields[cfg.routeParam] = [configKey])
    }
    if (cfg.routeQuery) {
      queryFields[cfg.routeQuery]
        ? queryFields[cfg.routeQuery].push(configKey)
        : (queryFields[cfg.routeQuery] = [configKey])
    }
  }

  const routeStatePush = async () => {
    const newRoute = {
      ...route,
      query: Object.assign({}, route.query),
      params: Object.assign({}, route.params),
      hash: String(route.hash)
    }
    let updated = false

    const stateKeys: (keyof T)[] = Object.keys(state)
    for (const stateKey of stateKeys) {
      const field = state[stateKey]
      const value = field.toRouteValue()

      if (field.routeQuery && value !== route.query[field.routeQuery]) {
        if (newRoute.query) {
          if (value) {
            newRoute.query[field.routeQuery] = value
          } else {
            delete newRoute.query[field.routeQuery]
          }
        } else if (value) {
          newRoute.query = { [field.routeQuery]: value }
        }
        updated = true
      }

      if (field.routeParam && value !== route.params[field.routeParam]) {
        if (newRoute.params) {
          if (value) {
            newRoute.params[field.routeParam] = value
          } else {
            delete newRoute.params[field.routeParam]
          }
        } else if (value) {
          newRoute.params = { [field.routeParam]: value }
        }
        updated = true
      }
    }

    if (updated) {
      await router.push(newRoute)
    }
    return updated
  }

  const routeStatePull = async () => {
    for (const [key, value] of Object.entries(queryFields)) {
      const routeValue = route.query[key]
      for (const stateKey of value) {
        const field = state[stateKey]
        field.pullRouteQueryValue(routeValue)
      }
    }

    for (const [key, value] of Object.entries(paramFields)) {
      const routeValue = route.params[key]
      for (const stateKey of value) {
        const field = state[stateKey]
        field.pullRouteParamValue(routeValue)
      }
    }
  }

  const fieldsUpdated = computed(() => {
    for (const k in state) {
      if (state[k].updated.value) {
        return true
      }
    }
    return false
  })

  const resetFieldsUpdated = () => {
    for (const k in state) {
      state[k].updated.value = false
    }
  }

  const resetValues = () => {
    for (const k in state) {
      state[k].resetValue()
    }
  }

  const resetErrors = () => {
    for (const k in state) {
      state[k].resetError()
    }
  }

  return {
    state,
    routeStatePush,
    routeStatePull,
    fieldsUpdated,
    resetFieldsUpdated,
    resetValues,
    resetErrors
  } as UseViewStateReturn<T>
}
